import React, { startTransition } from 'react';
import {
  Drawer as MuiDrawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  styled,
  Theme,
  CSSObject,
  Divider,
  Box,
  IconButton,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { NavItem } from './nav.types';
import {
  ExpandLess,
  ExpandMore,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { useAppState } from 'src/state-management/state-provider';
import FolderSharedIcon from '@mui/icons-material/FolderShared';
import { AllowedDataRoomProjectIds } from 'src/enum';

const drawerWidth = 240;
const headerHeight = 73; // Height of the header

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => {
  const commonPaperStyles = {
    marginTop: `${headerHeight + 1}px`,
    height: `calc(100% - ${headerHeight + 1}px)`,
    boxSizing: 'border-box' as const,
  };

  return {
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    position: 'fixed',
    zIndex: theme.zIndex.drawer,
    ...(open && {
      ...openedMixin(theme),
      '& .MuiDrawer-paper': {
        ...openedMixin(theme),
        ...commonPaperStyles,
      },
    }),
    ...(!open && {
      ...closedMixin(theme),
      '& .MuiDrawer-paper': {
        ...closedMixin(theme),
        ...commonPaperStyles,
      },
    }),
  };
});

interface SidebarProps {
  open: boolean;
  onToggle: () => void;
  navItems: NavItem[];
}

const Sidebar: React.FC<SidebarProps> = ({ open, onToggle, navItems }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [openMenus, setOpenMenus] = React.useState<string[]>([]);
  const { state } = useAppState();
  const projectId = state.selectedProject?.projectDetails?.project_id;
  const projectDetails = state.selectedProject?.projectDetails;

  const handleMenuClick = (path: string) => {
    startTransition(() => {
      setTimeout(() => {
        navigate(path);
      }, 300);
    });
    if (open) {
      onToggle();
    }
  };

  const handleSubMenuClick = (text: string) => {
    setOpenMenus((prevOpenMenus) =>
      prevOpenMenus.includes(text)
        ? prevOpenMenus.filter((t) => t !== text)
        : [...prevOpenMenus, text],
    );
  };

  const renderNavItems = (items: NavItem[], isSubmenu = false) => {
    // Inject the Data Room entry only for the gated project (Tata Motors),
    // and only at the top level of the menu.
    let itemsToRender = items;
    if (!isSubmenu && projectId && AllowedDataRoomProjectIds.includes(projectId)) {
      const alreadyPresent = items.some((i) => i.path === '/project/:id/data-room');
      if (!alreadyPresent) {
        itemsToRender = [
          ...items,
          { text: 'Data Room', path: '/project/:id/data-room', icon: FolderSharedIcon },
        ];
      }
    }

    const filteredItems = itemsToRender.filter((item) => {
      if (!item.moduleKey) {
        return true;
      }
      if (!projectDetails) {
        return false;
      }
      return !!projectDetails[item.moduleKey as keyof typeof projectDetails];
    });

    // New helper function
    const isDescendantSelected = (navItem: NavItem): boolean => {
      let currentPath = navItem.path;
      if (currentPath && currentPath.includes(':id') && projectId) {
        currentPath = currentPath.replace(':id', projectId);
      }

      if (currentPath && location.pathname.startsWith(currentPath)) {
        return true;
      }
      if (navItem.children) {
        return navItem.children.some(child => isDescendantSelected(child));
      }
      return false;
    };

    return filteredItems.map((item, index) => {
      if (item.divider) {
        return <Divider key={`divider-${index}`} sx={{ my: 1 }} />;
      }

      if (!item.icon || !item.text || !item.path) {
        return null;
      }

      const Icon = item.icon;
      const isMenuOpen = openMenus.includes(item.text);

      const isDynamic = item.path.includes(':id');
      const isDisabled = isDynamic && !projectId;

      let finalPath = item.path;
      if (isDynamic && projectId) {
        finalPath = item.path.replace(':id', projectId);
      }

      const isSelected = !isDisabled && location.pathname.startsWith(finalPath);

      if (item.children) {
        const isParentSelected = isDescendantSelected(item); // New variable

        return (
          <ListItem key={item.text} disablePadding sx={{ display: 'block' }}>
            <ListItemButton
              onClick={() => item.text && handleSubMenuClick(item.text)}
              selected={isParentSelected} // Modified line
              sx={{
                minHeight: 48,
                justifyContent: open ? 'initial' : 'center',
                px: 2.5,
                '&:hover': {
                  backgroundColor: '#419249',
                  color: 'white',
                  '& .MuiListItemIcon-root': {
                    color: 'white',
                  },
                },
                '&.Mui-selected': {
                  backgroundColor: '#419249',
                  color: 'white',
                  '& .MuiListItemIcon-root': {
                    color: 'white',
                  },
                },
                // Ensure selected style persists on hover
                '&.Mui-selected:hover': {
                  backgroundColor: '#419249',
                  color: 'white',
                  '& .MuiListItemIcon-root': {
                    color: 'white',
                  },
                },
              }}
              disabled={isDisabled}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: open ? 1.5 : 'auto',
                  justifyContent: 'center',
                }}
              >
                <Icon />
              </ListItemIcon>
              <ListItemText primary={item.text} sx={{ opacity: open ? 1 : 0 }} />
              {open && (isMenuOpen ? <ExpandLess /> : <ExpandMore />)}
            </ListItemButton>
            <Collapse in={isMenuOpen && open} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {renderNavItems(item.children, true)}
              </List>
            </Collapse>
          </ListItem>
        );
      }

      return (
        <ListItem key={item.text} disablePadding sx={{ display: 'block' }}>
          <ListItemButton
            selected={isSelected}
            disabled={isDisabled}
            onClick={() => !isDisabled && handleMenuClick(finalPath)}
            sx={{
              minHeight: 48,
              justifyContent: open ? 'initial' : 'center',
              px: 2.5,
              pl: isSubmenu && open ? 4 : 2.5,
              '&:hover': {
                backgroundColor: '#419249',
                color: 'white',
                '& .MuiListItemIcon-root': {
                  color: 'white',
                },
              },
              '&.Mui-selected': {
                backgroundColor: '#419249',
                color: 'white',
                '& .MuiListItemIcon-root': {
                  color: 'white',
                },
              },
              // Ensure selected style persists on hover
              '&.Mui-selected:hover': {
                backgroundColor: '#419249',
                color: 'white',
                '& .MuiListItemIcon-root': {
                  color: 'white',
                },
              },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: open ? 1.5 : 'auto',
                justifyContent: 'center',
              }}
            >
              <Icon />
            </ListItemIcon>
            <ListItemText primary={item.text} sx={{ opacity: open ? 1 : 0 }} />
          </ListItemButton>
        </ListItem>
      );
    });
  };

  return (
    <Drawer variant="permanent" open={open}>
      <List>{renderNavItems(navItems)}</List>
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          borderTop: '1px solid rgba(0, 0, 0, 0.12)',
        }}
      >
        <IconButton
          onClick={onToggle}
          sx={{
            borderRadius: 0,
            width: '100%',
            '&:hover': {
              backgroundColor: '#419249',
              color: 'white',
            },
          }}
        >
          {open ? <ChevronLeftIcon /> : <ChevronRightIcon />}
        </IconButton>
      </Box>
    </Drawer>
  );
};

export default Sidebar;